<template>
  <component
    :is="layoutComponent"
    v-if="show"
    :model="store.status"
    :insurance-plan="layoutInsurancePlan"
    :physicians-url="physiciansUrl"
    :physician-path="physicianPath"
    :plans-url="layoutPlansUrl"
    :accepted-label="acceptedLabel"
    :non-clearable="nonClearable"
    @insurance-changed="updateInsurancePlan"
  />
</template>

<script setup>
import { useSessionStorage } from "@vueuse/core";
import { computed, onMounted, ref, watch } from "vue";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { formAssociatedHost } from "#ce/framework/utils";
import { booleanAttribute, toBoolean } from "#ce/utils/props";
import CompactLayout from "./components/CompactLayout.ce.vue";
import TinyLayout from "./components/TinyLayout.ce.vue";
import WideLayout from "./components/WideLayout.ce.vue";
import { whenI18nReady } from "./i18n";
import { useSharedStatusStore, useStatusStore } from "./store/status";

// Opt into form association: the nested InsurancePlanFilter submits via useForm() on this app's host.
defineOptions(formAssociatedHost());

const INSURANCE_PLAN_SESSION_STORAGE_KEY = "insurancePlanFilter";

defineEmits(["change"]);
const { emitBubbling } = useHostEmit();

const props = defineProps({
  status: {
    type: String,
    default: undefined,
    validator: (value) => ["accepted", "no-provider-data", "undefined", "not-accepted"].includes(value),
  },
  insurancePlan: {
    type: String,
    default: undefined,
  },
  provider: {
    type: String,
    default: undefined,
  },
  api: {
    type: String,
    default: undefined,
  },
  variant: {
    type: String,
    default: "wide",
    validator: (value) => ["tiny", "compact", "wide"].includes(value),
  },
  static: booleanAttribute(),
  // When enabled, the element reads its initial insurance plan from
  // sessionStorage (when no `insurance-plan` is provided) and writes the
  // visitor's selection back, keeping it in sync across pages and across other
  // `sp-insurance-app` elements on the same page. Clients that disable
  // `persist_insurance_plan` simply leave this off.
  persist: {
    type: [Boolean, String],
    default: false,
  },
  physiciansUrl: {
    type: String,
    default: undefined,
  },
  physicianPath: {
    type: String,
    default: undefined,
  },
  // The endpoint the insurance filter loads the plan list from.
  plansUrl: {
    type: String,
    default: undefined,
  },
  acceptedLabel: {
    type: String,
    default: "Accepts Your Insurance",
  },
  // When enabled the insurance plan can't be cleared (legal requirement, see
  // `<sp-required-filters-modal>`). Threaded down to the InsurancePlanFilter.
  nonClearable: booleanAttribute(),
});

const layouts = {
  tiny: TinyLayout,
  compact: CompactLayout,
  wide: WideLayout,
};

const layoutComponent = computed(() => layouts[props.variant] ?? WideLayout);

// Only the wide/compact layouts host the insurance filter. TinyLayout doesn't
// declare an `insurance-plan` or `plans-url` prop, so binding them there would leak as stray
// attributes onto its root `<sp-alert>`.
const layoutInsurancePlan = computed(() => (props.variant === "tiny" ? undefined : currentInsurancePlan.value));
const layoutPlansUrl = computed(() => (props.variant === "tiny" ? undefined : props.plansUrl));

// Reactive bridge to sessionStorage, keeping the chosen plan in sync across
// reloads and across sibling `sp-insurance-app` elements. Only created when the
// client opts in via `persist`; otherwise storage is never touched. `null` is
// the empty sentinel; falsy values are coerced to `undefined` for the layouts.
const storedInsurancePlan = toBoolean(props.persist)
  ? useSessionStorage(INSURANCE_PLAN_SESSION_STORAGE_KEY, null)
  : null;

// An explicit `insurance-plan` attribute always wins; otherwise fall back to
// the persisted plan when enabled.
const currentInsurancePlan = ref(props.insurancePlan || storedInsurancePlan?.value || undefined);

const store = toBoolean(props.static) ? useStatusStore(props) : useSharedStatusStore({ url: props.api });

const render = ref(false);
const show = computed(() => render.value && store.status !== null);

async function loadStatus() {
  // Load the status from the API if it's provided
  if (props.api && currentInsurancePlan.value !== null) {
    await store.load?.(currentInsurancePlan.value);
  }
}

function updateInsurancePlan(value) {
  currentInsurancePlan.value = value;
  if (storedInsurancePlan) {
    // Drop the key when the selection is cleared rather than storing an empty value.
    storedInsurancePlan.value = value || null;
  }
  emitBubbling("change", value);
}

watch(
  () => props.insurancePlan,
  (newVal) => (currentInsurancePlan.value = newVal),
);

// Reflect external/sibling updates to the persisted plan.
if (storedInsurancePlan) {
  watch(storedInsurancePlan, (value) => (currentInsurancePlan.value = value || undefined));
}

watch(currentInsurancePlan, loadStatus);

onMounted(async () => {
  await Promise.all([loadStatus(), whenI18nReady]);

  render.value = true;
});
</script>

<style>
:host {
  display: block;
}

:host(:focus) {
  outline: none;
  box-shadow: none !important;
}
</style>
